import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import {
  INSERT_RESOURCE_LIST_TEMPLATE_SECTION_COMMAND,
  ResourceTemplateSectionSchema,
  ResourceTemplateSectionIdAttr,
  ResourceTemplateIdsAttr,
  ResourceTemplateSectionTypeAttr,
  ResourceTemplateSectionFilterTypeAttr,
  ResourceTemplateSectionFilterWeeksRangeAttr,
  ResourceTemplateSectionFilterRangeStrAttr,
} from './constants';
import { InsertResourceListSectionCommand } from './insert-resource-list-section-command';
import styles from './resource-list-section.module.css';
import { ResourcelistSectionTemplatePluginConfiguration } from './types';

/**
 * Plugin to create a placeholder kinda html where actual tasks can be
 * populated from "server" itself. ie, you can see some future data holders using handlebar template
 * and server will populate these fields with correct data before sending this to clients.
 * But as far this plugin is concerned, it's renders a simple UI with some data- attributes.
 */
export class ResourceListTemplateSection extends Plugin {
  static get requires() {
    return [Widget];
  }

  static get pluginName(): string {
    return 'ResourceListTemplateSection';
  }

  init() {
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add(
      INSERT_RESOURCE_LIST_TEMPLATE_SECTION_COMMAND,
      new InsertResourceListSectionCommand(this.editor),
    );
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    schema.register(ResourceTemplateSectionSchema, {
      // Behaves like a self-contained object (e.g. an image).
      isObject: true,

      // Allow in places where other blocks are allowed (e.g. directly in the root).
      allowWhere: '$block',
      allowAttributes: [
        ResourceTemplateSectionIdAttr,
        ResourceTemplateIdsAttr,
        ResourceTemplateSectionTypeAttr,
        ResourceTemplateSectionFilterTypeAttr,
        ResourceTemplateSectionFilterWeeksRangeAttr,
        ResourceTemplateSectionFilterRangeStrAttr,
      ],
    });
  }

  private defineConverters() {
    const { editor } = this;
    const { conversion } = editor;
    const renderResourceListSection =
      editor.config.get<ResourcelistSectionTemplatePluginConfiguration>(
        ResourceTemplateSectionSchema,
      ).renderer;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'section',
        classes: [ResourceTemplateSectionSchema],
      },
      model: (viewElement, { writer: modelWriter }) => {
        const resourceTemplateSectionId = viewElement.getAttribute(
          `data-${ResourceTemplateSectionIdAttr}`,
        );
        const resourceTemplateSectionType = viewElement.getAttribute(
          `data-${ResourceTemplateSectionTypeAttr}`,
        );

        return modelWriter.createElement(ResourceTemplateSectionSchema, {
          [ResourceTemplateIdsAttr]: viewElement.getAttribute(
            `data-${ResourceTemplateIdsAttr}`,
          ),
          [ResourceTemplateSectionIdAttr]: resourceTemplateSectionId,
          [ResourceTemplateSectionTypeAttr]: resourceTemplateSectionType,
          [ResourceTemplateSectionFilterTypeAttr]: viewElement.getAttribute(
            `data-${ResourceTemplateSectionFilterTypeAttr}`,
          ),
          [ResourceTemplateSectionFilterRangeStrAttr]: viewElement.getAttribute(
            `data-${ResourceTemplateSectionFilterRangeStrAttr}`,
          ),
          [ResourceTemplateSectionFilterWeeksRangeAttr]:
            viewElement.getAttribute(
              `data-${ResourceTemplateSectionFilterWeeksRangeAttr}`,
            ),
        });
      },
    });

    conversion.for('editingDowncast').elementToElement({
      model: ResourceTemplateSectionSchema,
      view: (modelElement, { writer: viewWriter }) => {
        const resourceTemplateIds = modelElement.getAttribute(
          ResourceTemplateIdsAttr,
        );

        const resourceTemplateSectionId = modelElement.getAttribute(
          ResourceTemplateSectionIdAttr,
        );
        const resourceTemplateSectionType = modelElement.getAttribute(
          ResourceTemplateSectionTypeAttr,
        );
        const resourceTemplateSectionFilterType = modelElement.getAttribute(
          ResourceTemplateSectionFilterTypeAttr,
        );
        const resourceTemplateSectionFilterWeeksRange =
          modelElement.getAttribute(
            ResourceTemplateSectionFilterWeeksRangeAttr,
          );
        const resourceTemplateSectionFilterRangeStr = modelElement.getAttribute(
          ResourceTemplateSectionFilterRangeStrAttr,
        );
        const reactWrapper = viewWriter.createUIElement(
          'section',
          {
            class: `${ResourceTemplateSectionSchema}__react-wrapper`,
          },
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderResourceListSection(
              {
                resourceTemplateSectionId,
                resourceTemplateSectionType,
                resourceTemplateSectionFilterType,
                resourceTemplateSectionFilterWeeksRange,
                resourceTemplateSectionFilterRangeStr,
              },
              domElement,
              editor,
            );

            return domElement;
          },
        );
        const resourceTemplateSectionView = viewWriter.createContainerElement(
          'section',
          {
            class: `${ResourceTemplateSectionSchema} ${styles.section}`,
            [`data-${ResourceTemplateIdsAttr}`]: resourceTemplateIds,
            [`data-${ResourceTemplateSectionIdAttr}`]:
              resourceTemplateSectionId,
            [`data-${ResourceTemplateSectionTypeAttr}`]:
              resourceTemplateSectionType,
            [`data-${ResourceTemplateSectionFilterTypeAttr}`]:
              resourceTemplateSectionFilterType,
            [`data-${ResourceTemplateSectionFilterRangeStrAttr}`]:
              resourceTemplateSectionFilterRangeStr,
            [`data-${ResourceTemplateSectionFilterWeeksRangeAttr}`]:
              resourceTemplateSectionFilterWeeksRange,

            'data-cke-ignore-events': true,
          },
          [reactWrapper],
        );

        return toWidget(resourceTemplateSectionView, viewWriter);
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: ResourceTemplateSectionSchema,
      view: (modelItem, { writer: viewWriter }) => {
        const resourceTemplateIds = modelItem.getAttribute(
          ResourceTemplateIdsAttr,
        );
        const resourceTemplateSectionId = modelItem.getAttribute(
          ResourceTemplateSectionIdAttr,
        );
        const resourceTemplateSectionType = modelItem.getAttribute(
          ResourceTemplateSectionTypeAttr,
        );
        const resourceTemplateSectionFilterType = modelItem.getAttribute(
          ResourceTemplateSectionFilterTypeAttr,
        );
        const resourceTemplateSectionFilterWeeksRange = modelItem.getAttribute(
          ResourceTemplateSectionFilterWeeksRangeAttr,
        );
        const resourceTemplateSectionFilterRange = modelItem.getAttribute(
          ResourceTemplateSectionFilterRangeStrAttr,
        );
        const resourceTemplateSectionEl = viewWriter.createRawElement(
          'section',
          {
            class: `${styles.section} ${styles.section_loading}`,
          },
        );

        const resourceTemplateSectionView = viewWriter.createContainerElement(
          'section',
          {
            class: ResourceTemplateSectionSchema,
            [`data-${ResourceTemplateIdsAttr}`]: resourceTemplateIds,
            [`data-${ResourceTemplateSectionIdAttr}`]:
              resourceTemplateSectionId,
            [`data-${ResourceTemplateSectionTypeAttr}`]:
              resourceTemplateSectionType,
            [`data-${ResourceTemplateSectionFilterTypeAttr}`]:
              resourceTemplateSectionFilterType,
            [`data-${ResourceTemplateSectionFilterRangeStrAttr}`]:
              resourceTemplateSectionFilterRange,
            [`data-${ResourceTemplateSectionFilterWeeksRangeAttr}`]:
              resourceTemplateSectionFilterWeeksRange,
          },
          [resourceTemplateSectionEl],
        );

        return resourceTemplateSectionView;
      },
    });
  }
}
